import React, { useContext, useState } from 'react';
import { navigate } from '@reach/router';
import { usePDF } from '@react-pdf/renderer';
import dayjs from 'dayjs';

import { Alert, AlertTitle } from '@material-ui/lab';
import Button from '@material-ui/core/Button'
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import LinearProgress from '@material-ui/core/LinearProgress';
import Paper from '@material-ui/core/Paper';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';

import { timestamp } from '../../../app/data/validate';
import AuthContext from '../../../services/firebase/AuthContext';
import { buildInvoiceData } from './PdfData';
import { createBlobAttachment } from '../../../services/firebase/StorageService';
import { FirebaseContext } from '../../../services/firebase/FirebaseContext';
import FireDataContext from '../../../services/firebase/FireDataContext';
import { getSalesInvoiceRecipientInfo } from './InvoiceData';
import JobData from '../_common/JobData';
import { keyGen } from '../../../services/keygen';
import { onLocalHost } from '../../../app/common/useful';
import SalesInvoicePDF from './SalesInvoicePDF';
import sendMail from '../../../services/firebase/MailService';
import Spacer, { Spaced } from '../../../app/common/ui/Spacer';

const fakeCreateInvoice = () => {
    return new Promise((resolve) => {
        resolve({
            data: {
                Invoices: [
                    {Type: 'ACCREC', InvoiceNumber: `INV-${keyGen(4)}`, InvoiceID: keyGen(10)},
                    {Type: 'ACCPAY', InvoiceID: keyGen(10)}
                ]
            }
        })
    });
}

const InvoiceSubmit = ({ digest }) => {
    const db = useContext(FireDataContext);
    const firebase = useContext(FirebaseContext);
    const createInvoice = onLocalHost() ? fakeCreateInvoice : firebase.functions().httpsCallable('createInvoice');
    const { currentUser } = useContext(AuthContext);
    const api = new JobData({ db, currentUser });
    const [accepted, setAccepted] = useState(false);
    const [message, setMessage] = useState();
    const [busy, setBusy] = useState(false);

    const { timesheet, intermediary, referral, courtCase, client, referrer, organization } = digest;

    const recipient = timesheet.invoicee || getSalesInvoiceRecipientInfo({ referrer, organization, tariff: timesheet.tariff, items: timesheet.items });

    const salesInvoice = {
        id: timesheet.id,
        invoiceType: 'sales',
        reference: keyGen(6),
        timesheet, intermediary: { profile: intermediary.profile }, referral, courtCase, client,
        submittedAt: dayjs().valueOf()
    };

    const purchaseInvoice = {
        id: timesheet.id,
        invoiceType: 'purchase',
        reference: referral.bookingReference,
        timesheet: { ...timesheet, tariff: { ...timesheet.tariff, taxRate: intermediary.profile.vatNumber ? timesheet.tariff.taxRate : 0.0 } },
        intermediary: { profile: intermediary.profile }, referral, courtCase, client,
        submittedAt: dayjs().valueOf(),
    };

    const data = buildInvoiceData({ digest, invoiceType: 'sales', reference: salesInvoice.reference });

    const invoice = <SalesInvoicePDF {...data} submittedAt={dayjs().valueOf()} />;
    const [pdf, updatePDF] = usePDF({ document: invoice });

    const updatePdfDoc = () => {
        return new Promise((resolve, reject) => {
            try {
                updatePDF();
                while (pdf.loading) {
                    // yuk
                };
                resolve(true);
            }
            catch (error) {
                reject(error);
            }
        })
    }

    const date = dayjs();
    const dueDate = date.add(30, 'days');

    const accrec = {
        Type: "ACCREC",
        Contact: {
            ContactID: recipient.xeroContactId
        },
        Date: date.format('YYYY-MM-DD'),
        DueDate: dueDate.format('YYYY-MM-DD'),
        Url: `https://ti-coop.web.app/organizations/WHuoyDs7zcB8wUU7euiC/sales_invoices/${timesheet.id}`,
        Reference: referral.bookingReference,
        Status: "SUBMITTED",
        LineItems: salesInvoice.timesheet.items.filter(({ amount }) => amount > 0).map(({ description, quantity, rate }) => {
            return {
                Description: description,
                Quantity: quantity,
                UnitAmount: rate,
                AccountCode: "200"
            }
        })
    };

    // TODO: confirm that INPUT2 is correct for VAT registered members
    const TaxType = intermediary.profile.vatNumber ? 'INPUT2' : 'NONE';

    const accpay = {
        Type: "ACCPAY",
        Contact: {
            ContactID: currentUser.xeroContactId
        },
        Date: date.format('YYYY-MM-DD'),
        DueDate: dueDate.add(7, 'days').format('YYYY-MM-DD'),
        Url: `https://ti-coop.web.app/organizations/WHuoyDs7zcB8wUU7euiC/purchase_invoices/${timesheet.id}`,
        Reference: referral.bookingReference,
        Status: "SUBMITTED",
        LineItems: purchaseInvoice.timesheet.items.filter(({ amount }) => amount > 0).map(item => {
            var rate = 0.0;

            if (item.key === 'ADMIN01') {
                rate = 0.0;
            }
            else {
                rate = item.category === 'services' ? timesheet.tariff.services[item.section][item.code].rate : item.rate;
            }
            
            return {
                Description: item.description,
                Quantity: item.quantity,
                UnitAmount: rate,
                AccountCode: "310",
                TaxType
            }
        })
    };

    const handleSubmitClick = () => {
        
        (async () => {
            try {
                setBusy(true);

                // these defaults will be overidden if createInvoice works 
                salesInvoice.xeroInvoiceId = '!?';
                salesInvoice.originalReference = salesInvoice.reference;
                salesInvoice.reference = `!${keyGen(6)}!`;
                purchaseInvoice.xeroInvoiceId ='!?';

                try {
                    const result = await createInvoice({ Invoices: [{ ...accrec }, { ...accpay }] });
                    const siResult = result.data.Invoices.find(i => i.Type === 'ACCREC');
                    const piResult = result.data.Invoices.find(i => i.Type === 'ACCPAY');
    
                    salesInvoice.xeroInvoiceId = siResult?.InvoiceID ?? '?';
                    salesInvoice.temporaryReference = salesInvoice.reference;
                    salesInvoice.reference = siResult?.InvoiceNumber ?? '?';
    
                    // XERO doesn't generate an invoice number for bills
                    purchaseInvoice.xeroInvoiceId = piResult?.InvoiceID ?? '?';
                }
                catch (error) {
                    console.log(error)
                    sendMail({ 
                        db,
                        uid: currentUser.uid,
                        to: 'chris.dodds@synescent.com',
                        cc: 'accounts@theintermediarycooperative.co.uk',
                        message: {
                            subject: `There was a problem creating the Xero invoices for booking reference ${referral.bookingReference} 🧨💥🔥`,
                            text: error.message ?? 'Unknown error 🔥🔥🔥',
                            html: `<div>${error.message}</div>`
                        }
                    });
                }

                salesInvoice.court = timesheet.court || {};
                purchaseInvoice.salesInvoiceRef = salesInvoice.reference;

                const purchaseInvoicePath = await api.saveInvoice({ invoice: purchaseInvoice });
                const salesInvoicePath = await api.saveInvoice({ invoice: salesInvoice });

                const manifest = {
                    purchaseInvoicePath,
                    salesInvoicePath,
                    ts: timestamp(),
                    uid: currentUser.uid
                };

                await db.update({
                    path: timesheet.bookingPath,
                    payload: {
                        completed: manifest
                    }
                });

                await db.update({
                    path: timesheet._docPath,
                    payload: {
                        submitted: manifest
                    }
                });

                await updatePdfDoc();
                const attachment = await createBlobAttachment({ fullPath: `finance/accrec/invoice-${salesInvoice.reference.toLowerCase()}.pdf`, blob: pdf.blob });

                const settings = await db.get({ path: 'config/settings'}); 
                // notify if feature enabled
                if (settings?.enableInvoiceMail?.value || onLocalHost()) {

                    console.log('sending email');

                    await sendMail({
                        db, uid: currentUser.uid,
                        to: onLocalHost() ? 'chris@bodycheetah.com' : recipient.email,
                        cc: recipient.email === 'APinvoices-CTS-U@gov.sscl.com' ? 'erin.holland@justice.gov.uk' : [],
                        bcc: onLocalHost() ? 'chris.dodds@factivated.com' : 'accounts@theintermediarycooperative.co.uk',
                        message: {
                            subject: `Your invoice for booking reference ${referral.bookingReference} Case Number ${courtCase.courtCaseNumber}`,
                            html: `<div>Your invoice is attached</div>`,
                            text: `Your invoice is attached`,
                            attachments: [
                                {
                                    path: attachment
                                }
                            ]
                        }
                    });

                }    
                setBusy(false);
                setMessage({ severity: 'success', title: 'Success!', text: 'Your invoice has been submitted' });
            }
            catch (error) {
                console.log(error);
                setBusy(false);
                setMessage({ severity: 'error', title: 'Something went wrong!', text: error.message });
            }
        })();
    }

    const handleCloseClick = () => {
        navigate("/");
    }

    if (!recipient.xeroContactId && !currentUser.xeroContactId) {
        // slack.adminMessage(`Hi! ${currentUser.name} is trying to create an invoice for ${organization.name} so we need to link them both to XERO https://ti-coop.web.app/admin/finance/xorg`);
        
        return <Alert severity="error" variant="outlined">
            <AlertTitle>Accounts action required</AlertTitle>
            Please contact <a href='mailto:accounts@theintermediarycooperative.co.uk'>accounts@theintermediarycooperative.co.uk</a> and ask them to set up a XERO link for {organization.name} and your member account so that we can create your invoices
        </Alert>
    }
    else if (!recipient.xeroContactId) {
        // slack.adminMessage(`Hi! ${currentUser.name} is trying to create an invoice for ${organization.name} and needs the referrer linked to XERO https://ti-coop.web.app/admin/finance/xorg`);

        return <Alert severity="error" variant="outlined">
            <AlertTitle>Accounts action required</AlertTitle>
            Please contact  <a href='mailto:accounts@theintermediarycooperative.co.uk'>accounts@theintermediarycooperative.co.uk</a> and ask them to set up a XERO link for {organization.name} so that we can create your invoices
        </Alert>
    }
    else if (!currentUser.xeroContactId) {
        // slack.adminMessage(`Hi! ${currentUser.name} is trying to create an invoice and needs their member account linked to XERO https://ti-coop.web.app/admin/finance/xmembers`);
        
        return <Alert severity="error" variant="outlined">
            <AlertTitle>Accounts action required</AlertTitle>
            Please contact <a href='mailto:accounts@theintermediarycooperative.co.uk'>accounts@theintermediarycooperative.co.uk</a> and ask them to set up a XERO link for your member account so that we can create your invoices
        </Alert>
    }
    else if (recipient.xeroContactId === '30e44179-a27b-42d5-8774-07d164b16aa7' && (!timesheet.court?.costCentre || timesheet.court?.costCentre === '00000000')) {
        // this is an HMCTS invoice so requires a cost centre
        return <Alert severity="warning" variant="filled">
            <AlertTitle>Court Cost Centre Required</AlertTitle>
            Please add the court and cost centre to your invoice so that HMCTS will pay it :)
        </Alert>
    }

    return (
        <Paper style={{ padding: '1rem' }}>
            <Grid container direction="column" spacing={1}>
                <Grid item>
                    <Typography variant="h6">Invoice Confirmation</Typography>
                </Grid>
                <Grid item>
                    <FormControlLabel
                        control={<Switch checked={accepted} onChange={event => setAccepted(event.target.checked)} color="primary" />}
                        label={`I confirm I've checked the invoice and it all looks correct`}
                    />
                </Grid>
                <Grid item>
                    {
                        !message &&
                        <React.Fragment>
                            <Button
                                onClick={handleSubmitClick}
                                variant="contained"
                                color="secondary"
                                disabled={!accepted || busy}>
                                Submit invoice for payment
                            </Button>
                            <Spacer />
                            <Typography variant="subtitle1" color="textSecondary">It can take a few seconds to submit the invoices so please be patient!</Typography>
                        </React.Fragment>
                    }
                    {
                        busy && <Spaced><LinearProgress /></Spaced>
                    }
                    {
                        message &&
                        <Alert severity={message.severity} variant="filled" onClose={handleCloseClick}><AlertTitle>{message.title}</AlertTitle>{message.text}</Alert>
                    }
                </Grid>
            </Grid>
        </Paper>
    );

};

export default InvoiceSubmit;